import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import { v4 as uuid } from 'uuid';
import { newMessage, removeMessage, updateSingleMessage } from "../../../actions";
import { submitAnswer } from "../../../utilities/Answer";
import { stopVoiceOutput } from "../../../services/shared";
import { updateAudioInputsState } from "../../../actions/environment.action";
import MicIcon from '@mui/icons-material/Mic';
import PauseIcon from '@mui/icons-material/Pause';

export default function Audio() {
  const [mid, setMid] = useState(uuid());
  const SILENCE_TIMEOUT = 2000; // 2 seconds of silence before considering inactive

  const dispatch = useDispatch();
  const environment = useSelector((state: any) => state?.environment);
  const isMicOn = environment.audioInput.isMicOn;
  const isListening = environment.audioInput.isListening;

  const messages = useSelector((state: any) => state?.messages);
  const activeQuestion = useSelector((state: any) => state?.environment.activeQuestion);

  const lastSpeechTime = useRef(Date.now());
  const inactivityTimer = useRef<NodeJS.Timeout>();

  const {
    transcript,
    resetTranscript,
    listening,
    interimTranscript,
    browserSupportsSpeechRecognition,
  } = useSpeechRecognition();

  // Temp keeping listener all time active
  useEffect(() => {
    SpeechRecognition.startListening({ continuous: true, language: 'en-US' });
    dispatch(updateAudioInputsState({
      isMicOn: true,
      isListening: true
    }));

    return () => {
      SpeechRecognition.abortListening();
    }
  }, [dispatch])

  /**
   * User has clicked on send button
   * 1. Submit the transcript for processing 
   * 2. Stop listening until the bot replies
   * 3. Set a new message id, so that next user message is a new message
   */
  const sendRecording = useCallback(async (mid: string, transcript: string) => {
    try {

      if (!transcript) {
        return;
      }
      resetTranscript();
      await SpeechRecognition.abortListening();
      await SpeechRecognition.stopListening();

      dispatch(updateAudioInputsState({ isMicOn: false, isListening: false }));

      await submitAnswer(transcript, '', null, false, '', mid);
      setMid(uuid());

    } catch (error) {
      console.log(`Error sending recording`);
    }
  }, [resetTranscript, dispatch]);

  /**
   * When user wants to change what has already been spoken
   * 1. We reset the transcript for the package.
   * 2. We remove the message in the store
   * 3. We set a new mid to the message.
   */
  const removeRecording = () => {
    resetTranscript();
    dispatch(removeMessage({ mid }));
    setMid(uuid());
  }

  /**
   * When user clicks on pause icon
   * 1. We set isListening to false
   * 2. An icon to turn on the mic is displayed
   * 3. We turn off listening from the package.
   */
  const pauseListening = useCallback(async () => {
    try {
      await SpeechRecognition.abortListening();
      dispatch(updateAudioInputsState({
        isListening: false
      }));
    } catch (error) {
      console.log(`Mic couldn't be pause`);
    }
  }, [dispatch]);


  useEffect(() => {
    if (environment?.isVoiceOutputEnabled) {
      SpeechRecognition.startListening({ continuous: true, language: 'en-US' });
      lastSpeechTime.current = Date.now();
      dispatch(updateAudioInputsState({
        isMicOn: true,
        isListening: true
      }));
      return;
    }

  }, [environment?.isVoiceOutputEnabled, dispatch])

  /**
   * 1. Reset timer when new speech is detected
   * 2. Clear existing timer
   */
  useEffect(() => {
    if (transcript) {
      lastSpeechTime.current = Date.now();

      if (inactivityTimer.current) {
        clearTimeout(inactivityTimer.current);
      }
    }
  }, [transcript]);


  useEffect(() => {
    if (!listening || !transcript) return;
    const currentTimer = inactivityTimer.current;

    const checkInactivity = () => {
      const timeSinceLastSpeech = Date.now() - lastSpeechTime.current;
      if ((timeSinceLastSpeech >= SILENCE_TIMEOUT) && !!transcript) {
        sendRecording(mid, transcript);
      }
    };

    // Check user inactivity every second
    const intervalId = setInterval(checkInactivity, 1000);

    return () => {
      clearInterval(intervalId);
      if (currentTimer) {
        clearTimeout(currentTimer);
      }
    };
  });

  /**
   * This hook renders when the componenet is initialised for the first time.
   * 1. Reset any transcript present
   * 2. Stop listening to avoid any memory leakage
   */
  useEffect(() => {
    resetTranscript();
    stopVoiceOutput();
    // eslint-disable-next-line
  }, []);

  const addMessage = useCallback((transcript: string) => {
    const exists = messages.find((message: any) => message.mid === mid);
    if (interimTranscript) {
      stopVoiceOutput();
      if (exists) {
        dispatch(updateSingleMessage({
          label: transcript,
          position: 'right',
          type: 'STATEMENT',
          mid: mid,
          questionId: activeQuestion?.id,
          isTemp: true
        }));
      } else {
        dispatch(newMessage({
          label: interimTranscript,
          position: 'right',
          type: 'STATEMENT',
          mid: mid,
          questionId: activeQuestion?.id,
          isTemp: true
        }));
      }
    }
  }, [activeQuestion?.id, interimTranscript, messages, mid, dispatch]);


  /**
   * This hooks executes whenever transcript changes where transcript is the speech synthesiser transcsript
   * 1. Check if the mid exists in the messages array in the store
   * 2. If not create a dispatch a new one
   * 3. If exists update the existing one
   */
  useEffect(() => {
    addMessage(transcript);
    // eslint-disable-next-line
  }, [transcript]);

  if (!browserSupportsSpeechRecognition) {
    return <p>Your browser does not support speech recognition. Switch to text mode.</p>;
  }

  const turnMicOn = () => {
    lastSpeechTime.current = Date.now();
    SpeechRecognition.startListening({ continuous: true, language: 'en-US' })
      .then(() => {
        dispatch(updateAudioInputsState({
          isMicOn: true,
          isListening: true
        }));
      })
      .catch(error => console.log(error));
  }

  return (
    <>
      {
        isMicOn && (
          <div className='voice-controls flex justify-center items-center h-voice'>
            {
              !isListening && (
                <img rel="preload" alt="mic-on" onClick={() => {
                  SpeechRecognition.startListening({ continuous: true, language: 'en-US' });
                }} className="cursor-pointer mr-2" src={process.env.REACT_APP_CDN + "/voice-paused.svg"}></img>
              )
            }
            {
              isListening && (
                <button className="bg-secondaryBgColor btn relative rounded-full p-bpPadding10 mx-2" onClick={pauseListening}>
                  <PauseIcon fontSize="large" sx={{ color: "#666" }}></PauseIcon>
                </button>
                /* Commented as part of experiments on voice bots */
                // <img onClick={pauseListening} alt="pause" className="cursor-pointer mx-2" src={process.env.REACT_APP_CDN + "/pause.svg"}></img>
              )
            }
            {
              !!transcript && (
                <img onClick={removeRecording} alt="remove" className=" cursor-pointer" src={process.env.REACT_APP_CDN + "/delete.svg"}></img>
              )
            }
            {/* Commented as part of experiments on voice bots */}
            {/* <img onClick={sendRecording} alt="send" className="ml-2 cursor-pointer bg-bpPrimary rounded-full min-w-7 min-h-7 h-12 w-12" src={`${process.env.REACT_APP_CDN}/send-3.svg`} loading="lazy"></img> */}
          </div>

        )
      }

      {
        !isMicOn && (
          <div className='voice-input-container flex justify-center items-center h-voice'>
            {
              (environment?.typing || environment?.isVoiceOutputEnabled) &&
              <img onClick={() => SpeechRecognition.abortListening()} alt="mic-disabled" className="mx-4" src={process.env.REACT_APP_CDN + "/mic-disabled.svg"}></img>
            }
            {
              (!environment?.typing && !environment?.isVoiceOutputEnabled) &&
              (
                <>
                  <div className="flex flex-col justify-center items-center" >
                    <button id="speech" className="btn relative rounded-full bg-bpPrimary p-bpPadding10" onClick={() => { turnMicOn();  stopVoiceOutput(); }}>
                      <MicIcon fontSize="large" sx={{ color: "white" }}></MicIcon>
                    </button>
                    <p className="text-secondaryText my-2 mb-0">Tap to Speak</p>
                  </div>

                </>
              )
            }
          </div >
        )
      }
    </>
  )
}
